import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Trophy, FileText } from 'lucide-react';
import {
  Button,
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Alert,
  AlertDescription,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui';
import { Textarea } from '../ui/textarea';
import ImageUploader from '../ui/ImageUploader';
import { getAllLigas, getTemporadas } from '../../lib/supabase';
import { fixMojibake } from '../../lib/utils';
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import { useOrganization } from '../../contexts/OrganizationContext';
import { separarLigas } from '../../lib/ligasPorMundo';

const ESTATUS_OPTIONS = [
  { value: 'planeado', label: 'Planeado' },
  { value: 'en_curso', label: 'En Curso' },
  { value: 'finalizado', label: 'Finalizado' },
  { value: 'cancelado', label: 'Cancelado' }
];

const TIPO_TORNEO_OPTIONS = [
  { value: 'liga', label: 'Liga' },
  { value: 'copa', label: 'Copa' },
  { value: 'invitacional', label: 'Invitacional' },
  { value: 'amistoso', label: 'Amistoso' },
  { value: 'playoff', label: 'Playoff' },
  { value: 'otro', label: 'Otro' }
];

/**
 * FormTorneo - Formulario para crear/editar torneos
 *
 * @param {object} torneo - Datos del torneo a editar (null para crear nuevo)
 * @param {function} onSubmit - Callback al guardar (torneoData) => Promise<void>
 * @param {function} onCancel - Callback al cancelar
 * @param {boolean} saving - Estado de guardado
 */
const FormTorneo = ({
  torneo = null,
  onSubmit,
  onCancel,
  saving = false,
  open = true,
  onOpenChange
}) => {
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    liga_id: '',
    temporada_id: '',
    fecha_inicio: '',
    fecha_fin: '',
    sede: '',
    organizador: '',
    logo_url: '',
    flyer_bg_url: '',
    estatus: 'planeado',
    tipo_torneo: 'liga',
    ubicacion: ''
  });

  const [ligas, setLigas] = useState([]);
  const [loadingLigas, setLoadingLigas] = useState(true);
  const [temporadas, setTemporadas] = useState([]);
  const [loadingTemporadas, setLoadingTemporadas] = useState(true);
  const { org } = useOrganization();
  // Un torneo interno cuelga de una liga MIA: la que el club creo para nombrar la
  // competencia de afuera. Las de la red son de otra organizadora y no van aqui.
  const misLigas = separarLigas(ligas, org?.id).mias;

  const [error, setError] = useState(null);

  const handleClose = () => {
    if (saving) return;
    onCancel?.();
    onOpenChange?.(false);
  };

  const guard = useUnsavedGuard({ onClose: handleClose });

  useEffect(() => {
    loadLigas();
    loadTemporadas();
  }, []);

  useEffect(() => {
    if (open) {
      guard.resetDirty();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (torneo) {
      setFormData({
        nombre: torneo.nombre || '',
        descripcion: torneo.descripcion || '',
        liga_id: torneo.liga_id || '',
        temporada_id: torneo.temporada_id || '',
        fecha_inicio: torneo.fecha_inicio || '',
        fecha_fin: torneo.fecha_fin || '',
        sede: torneo.sede || '',
        organizador: torneo.organizador || '',
        logo_url: torneo.logo_url || '',
        flyer_bg_url: torneo.flyer_bg_url || '',
        estatus: torneo.estatus || 'planeado',
        tipo_torneo: torneo.tipo_torneo || 'liga',
        ubicacion: torneo.ubicacion || ''
      });
    }
  }, [torneo]);

  // La temporada es lo que permite leer el historico del grupo por año deportivo: sin
  // ella, los torneos de dos temporadas se suman en el mismo monton.
  const loadTemporadas = async () => {
    try {
      setLoadingTemporadas(true);
      const { data, error } = await getTemporadas();
      if (error) throw error;
      setTemporadas(data || []);
    } catch (e) {
      console.error('Error cargando temporadas:', e);
      setTemporadas([]);
    } finally {
      setLoadingTemporadas(false);
    }
  };

  const loadLigas = async () => {
    try {
      setLoadingLigas(true);
      const { data, error } = await getAllLigas();
      if (error) throw error;
      setLigas(data || []);
    } catch (err) {
      console.error('Error loading ligas:', err);
    } finally {
      setLoadingLigas(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validaciones
    if (!formData.nombre.trim()) {
      setError('El nombre del torneo es obligatorio');
      return;
    }

    if (!formData.fecha_inicio) {
      setError('La fecha de inicio es obligatoria');
      return;
    }

    if (formData.fecha_fin && formData.fecha_inicio > formData.fecha_fin) {
      setError('La fecha de fin no puede ser anterior a la fecha de inicio');
      return;
    }

    // Todo torneo interno pertenece a una temporada (el dueno, 2026-09-05): es lo que
    // permite leer el historico del grupo por ano deportivo. Un torneo viejo sin
    // temporada la pide al editarse.
    if (!formData.temporada_id) {
      setError('La temporada es obligatoria: todo torneo pertenece a una temporada');
      return;
    }

    try {
      await onSubmit(formData);
      guard.resetDirty();
    } catch (err) {
      setError(err.message || 'Error al guardar el torneo');
    }
  };

  return (
    <Dialog open={open} onOpenChange={guard.onOpenChange}>
      <DialogContent {...guard.dirtyProps} className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{torneo ? 'Editar Torneo' : 'Crear Nuevo Torneo'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Información Básica */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4" /> Información Básica
            </h4>
            <div className="space-y-2">
              <Label>Nombre del Torneo *</Label>
              <Input value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} placeholder="Ej: Torneo Apertura 2026" required disabled={saving} />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea value={formData.descripcion} onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })} rows={2} placeholder="Descripción breve del torneo..." disabled={saving} />
            </div>
            <div className="space-y-2">
              <Label>Liga / Organización</Label>
              <Select value={formData.liga_id || "sin_liga"} onValueChange={(value) => { guard.markDirty(); setFormData({ ...formData, liga_id: value === "sin_liga" ? null : value }); }} disabled={saving || loadingLigas}>
                <SelectTrigger><SelectValue placeholder={loadingLigas ? "Cargando..." : "Sin liga"} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sin_liga">Sin liga</SelectItem>
                  {misLigas.map((liga) => <SelectItem key={liga.id} value={liga.id}>{fixMojibake(liga.nombre)}</SelectItem>)}
                </SelectContent>
              </Select>
              {!loadingLigas && misLigas.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Tu club aún no tiene ligas. Regístralas en Torneos internos, Ligas.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Temporada *</Label>
              <Select
                value={formData.temporada_id || undefined}
                onValueChange={(value) => { guard.markDirty(); setFormData({ ...formData, temporada_id: value }); }}
                disabled={saving || loadingTemporadas}
              >
                <SelectTrigger><SelectValue placeholder={loadingTemporadas ? 'Cargando...' : 'Elige la temporada'} /></SelectTrigger>
                <SelectContent>
                  {temporadas.map((t) => <SelectItem key={t.id} value={t.id}>{fixMojibake(t.nombre)}</SelectItem>)}
                </SelectContent>
              </Select>
              {!loadingTemporadas && temporadas.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Tu club aún no tiene temporadas. Créala en Administración, Temporadas.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Todo torneo pertenece a una temporada: es lo que agrupa el historial del grupo.
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Tipo de Torneo</Label>
                <Select value={formData.tipo_torneo} onValueChange={(value) => { guard.markDirty(); setFormData({ ...formData, tipo_torneo: value }); }} disabled={saving}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPO_TORNEO_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Estatus</Label>
                <Select value={formData.estatus} onValueChange={(value) => { guard.markDirty(); setFormData({ ...formData, estatus: value }); }} disabled={saving}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ESTATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Fechas */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <Calendar className="h-4 w-4" /> Fechas
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Fecha de Inicio *</Label>
                <Input type="date" value={formData.fecha_inicio} onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })} required disabled={saving} />
              </div>
              <div className="space-y-2">
                <Label>Fecha de Fin</Label>
                <Input type="date" value={formData.fecha_fin} onChange={(e) => setFormData({ ...formData, fecha_fin: e.target.value })} min={formData.fecha_inicio} disabled={saving} />
              </div>
            </div>
          </div>

          {/* Ubicación */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <MapPin className="h-4 w-4" /> Ubicación y Organización
            </h4>
            <div className="space-y-2">
              <Label>Ubicación</Label>
              <Input value={formData.ubicacion} onChange={(e) => setFormData({ ...formData, ubicacion: e.target.value })} placeholder="Ej: Estadio Municipal" disabled={saving} />
            </div>
            <div className="space-y-2">
              <Label>Sede</Label>
              <Input value={formData.sede} onChange={(e) => setFormData({ ...formData, sede: e.target.value })} placeholder="Ej: Complejo Deportivo ABC" disabled={saving} />
            </div>
            <div className="space-y-2">
              <Label>Organizador</Label>
              <Input value={formData.organizador} onChange={(e) => setFormData({ ...formData, organizador: e.target.value })} placeholder="Ej: Asociación de Futbol Local" disabled={saving} />
            </div>
          </div>

          {/* El torneo interno tambien lleva escudo (el dueno, 2026-09-05). En el tarjeton
              manda este; si no hay, el de la liga. */}
          <ImageUploader
            value={formData.logo_url}
            onChange={(url) => { guard.markDirty(); setFormData({ ...formData, logo_url: url }); }}
            bucket="logos"
            folder="torneos"
            label="Logo del torneo (opcional)"
          />

          {/* La foto de fondo del rol de jornada (Compartir rol). Misma columna que usa la
              organizadora en su wizard. Se pinta con velo oscuro; se puede apagar al compartir. */}
          <ImageUploader
            value={formData.flyer_bg_url}
            onChange={(url) => { guard.markDirty(); setFormData({ ...formData, flyer_bg_url: url }); }}
            bucket="logos"
            folder="torneos"
            label="Imagen de fondo del rol (opcional)"
          />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => guard.onOpenChange(false)} disabled={saving}>Cancelar</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Guardando...' : torneo ? 'Actualizar' : 'Crear Torneo'}</Button>
          </DialogFooter>
        </form>
        {guard.DiscardDialog}
      </DialogContent>
    </Dialog>
  );
};

export default FormTorneo;


